import { useEffect, useMemo } from 'react'

import { useRequest } from 'ahooks'

import { queryHelpCatalogueTreeWithDocApi } from '@eastcoal/api/content/helpCatalogueMall/queryHelpCatalogueTreeWithDoc'
import { type HelpCatalogueEntity } from '@eastcoal/api/content/interface'

import { useUrlParams } from '@eastcoal/common/hooks/useUrlParams'
import { KeySet } from '@eastcoal/common/types/utilities'
import { findTreePaths } from '@eastcoal/common/utils/array'

import { useHomeStoreApi } from '../hooks/useStore'
import { HomeState } from '../store/interface'

const useStoreUpdater = <K extends KeySet<HomeState>>(
  key: K,
  value: HomeState[K]
) => {
  const { setState } = useHomeStoreApi()

  useEffect(() => {
    setState({
      [key]: value
    })
  }, [key, value, setState])
}

const DEFAULT_DATA: HelpCatalogueEntity[] = []

const findDefaultDocId = (data: HelpCatalogueEntity[]) => {
  const find = (list: HelpCatalogueEntity[]) => {
    for (const item of list) {
      if (item.doc) {
        return item.id
      }
      if (item.children) {
        const result = find(item.children)
        if (result) {
          return result
        }
      }
    }
    return ''
  }
  return find(data)
}

export const HomeStoreUpdater = () => {
  const {
    data = DEFAULT_DATA,
    error,
    loading,
    refreshAsync
  } = useRequest(queryHelpCatalogueTreeWithDocApi, {
    manual: false
  })

  useStoreUpdater('data', data)
  useStoreUpdater('error', error)
  useStoreUpdater('loading', loading)
  useStoreUpdater('refreshData', refreshAsync)

  const { id } = useUrlParams()
  const currentDocId = useMemo(() => {
    return id || findDefaultDocId(data)
  }, [id, data])
  const currentCatalogues = useMemo(() => {
    return findTreePaths(data, value => value.id === currentDocId)
  }, [data, currentDocId])

  useStoreUpdater('currentDocId', currentDocId)
  useStoreUpdater('currentCatalogues', currentCatalogues)

  return null
}
